"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type Keys = keyof typeof DEFAULT_DIRECTORY_VALUES;
import {
  CreateDirectoryProps,
  Directory,
  UnSavedDirectory,
  DirectorySyncProviders,
} from "../types";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../sso/utils/cssClassAssembler";
import Button from "../../shared/Button/index";
import Spacer from "../../shared/Spacer/index";
import Select from "../../shared/Select/index";
import InputField from "../../shared/inputs/InputField/index";
import SecretInputFormControl from "../../shared/inputs/SecretInputFormControl/index";
import { sendHTTPRequest } from "../../shared/http";
import Checkbox from "../../shared/Checkbox/index";
const DEFAULT_DIRECTORY_VALUES: UnSavedDirectory = {
  name: "",
  tenant: "",
  product: "",
  webhook_url: "",
  webhook_secret: "",
  type: "azure-scim-v2",
  google_domain: "",
  log_webhook_events: false,
};

function CreateDirectory(props: CreateDirectoryProps) {
  const [directory, setDirectory] = useState(() => DEFAULT_DIRECTORY_VALUES);

  const [showDomain, setShowDomain] = useState(() => false);

  const [isSaving, setIsSaving] = useState(() => false);

  function providers() {
    return Object.entries<string>(DirectorySyncProviders)
      ?.map(([value, text]) => ({
        value,
        text,
      }))
      .filter(({ value }) => {
        if (props.disableGoogleProvider) {
          return value !== "google";
        }
        return true;
      });
  }

  function setProvider(event: any) {
    const _val = event?.target?.value;
    if (_val === "google") {
      setShowDomain(true);
    } else {
      setShowDomain(false);
    }
    setDirectory({
      ...directory,
      type: _val,
    });
  }

  function classes() {
    return {
      fieldContainer: cssClassAssembler(
        props.classNames?.fieldContainer,
        defaultClasses.fieldContainer
      ),
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      select: {
        label: props.classNames?.label,
        select: props.classNames?.select,
      },
    };
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  function updateDirectory(data: Partial<typeof DEFAULT_DIRECTORY_VALUES>) {
    return {
      ...directory,
      ...data,
    };
  }

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setDirectory(
      updateDirectory({
        [target.id as Keys]: value,
      })
    );
  }

  function onSubmit(event: Event) {
    event.preventDefault();
    async function saveDirectory(body: any, url: string) {
      setIsSaving(true);
      const response = await sendHTTPRequest<{
        data: Directory;
      }>(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      setIsSaving(false);
      if (response && typeof response === "object") {
        if ("error" in response && response.error) {
          typeof props.errorCallback === "function" &&
            props.errorCallback(response.error.message);
        } else if ("data" in response && response.data) {
          typeof props.successCallback === "function" &&
            props.successCallback({
              operation: "CREATE",
              connection: response.data,
            });
        }
      }
    }
    saveDirectory(directory, props.urls.post);
  }

  function isExcluded(fieldName: keyof UnSavedDirectory) {
    return !!(props.excludeFields as (keyof UnSavedDirectory)[])?.includes(
      fieldName
    );
  }

  useEffect(() => {
    setDirectory(
      updateDirectory({
        tenant: props.tenant ?? directory.tenant,
        product: props.product ?? directory.product,
        webhook_url: props.defaultWebhookEndpoint ?? directory.webhook_url,
        webhook_secret: props.defaultWebhookSecret ?? directory.webhook_secret,
      })
    );
  }, [props.tenant, props.product, props.defaultWebhookEndpoint]);

  return (
    <div>
      {shouldDisplayHeader() ? (
        <h2 className={defaultClasses.heading}>Create Directory</h2>
      ) : null}
      <form onSubmit={(event) => onSubmit(event)}>
        {!isExcluded("name") ? (
          <>
            <InputField
              label="Directory name"
              id="name"
              name="name"
              value={directory.name}
              handleInputChange={handleChange}
              required
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("type") ? (
          <div className={classes().fieldContainer}>
            <Select
              label="Directory provider"
              name="type"
              id="type"
              classNames={classes().select}
              options={providers()}
              selectedValue={directory.type}
              handleChange={setProvider}
            />
            <Spacer y={6} />
          </div>
        ) : null}
        {showDomain ? (
          <>
            <InputField
              label="Directory domain"
              id="google_domain"
              name="google_domain"
              title="Please enter a valid domain (e.g: boxyhq.com)"
              value={directory.google_domain || ""}
              handleInputChange={handleChange}
              pattern={`^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$`}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("tenant") ? (
          <>
            <InputField
              label="Tenant"
              id="tenant"
              name="tenant"
              value={directory.tenant}
              handleInputChange={handleChange}
              required
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("product") ? (
          <>
            <InputField
              label="Product"
              id="product"
              name="product"
              value={directory.product}
              handleInputChange={handleChange}
              required
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("webhook_url") ? (
          <>
            <InputField
              type="url"
              label="Webhook URL"
              id="webhook_url"
              name="webhook_url"
              value={directory.webhook_url}
              handleInputChange={handleChange}
              classNames={classes().inputField}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("webhook_secret") ? (
          <>
            <SecretInputFormControl
              label="Webhook secret"
              id="webhook_secret"
              name="webhook_secret"
              value={directory.webhook_secret}
              handleChange={handleChange}
              classNames={classes().inputField}
              required={false}
              readOnly={false}
            />
            <Spacer y={6} />
          </>
        ) : null}
        {!isExcluded("log_webhook_events") ? (
          <div className={defaultClasses.checkboxFieldsDiv}>
            <Checkbox
              label="Enable Webhook events logging"
              id="log_webhook_events"
              name="log_webhook_events"
              checked={directory.log_webhook_events}
              handleChange={handleChange}
            />
            <Spacer y={6} />
          </div>
        ) : null}
        <div className={defaultClasses.formAction}>
          {typeof props.cancelCallback === "function" ? (
            <Button
              type="button"
              name="Cancel"
              variant="outline"
              handleClick={props.cancelCallback}
              classNames={props.classNames?.button?.cancel}
            />
          ) : null}
          <Button
            variant="primary"
            type="submit"
            name="Create Directory"
            classNames={props.classNames?.button?.ctoa}
            isLoading={isSaving}
          />
        </div>
      </form>
    </div>
  );
}

export default CreateDirectory;
